import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import debounceFunc from 'lodash/debounce';
import trans from 'translate';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { MESSAGE_BOX_TYPES } from 'constants/general';
import MessageBox from '../MessageBox/MessageBox';
import TokenListsSearchResult from './TokenListsSearchResult/TokenListsSearchResult';

import MagnifyingGlass from '../Icons/MagnifyingGlass';
import './TokensListsSearch.scss';
import ErrorListner from '../ErrorListner/ErrorListner';

import { getValueByMultiProps } from '../../../utils/mapper';
import { useOnClickOutside } from '../../../hooks/useClickAway';
import { fetchMostUsedTokensAction } from '../../../redux/actions/createHPoolActions';

const TokensListsSearch = ({
  placeholder,
  handleChange,
  debounceMs,
  reducerVal,
  searching,
  searchingErr,
  searchResults,
  handleSearchResultClick,
  selectedTokens,
  defaultTokens,
  fetchMostUsedTokensAction,
}) => {
  const [inputVal, setInputVal] = useState('');
  const [menuShown, setMenuShown] = useState(false);
  const searchMenuRef = useRef(null);

  const handleChangeDebounced = useRef(debounceFunc(handleChange, debounceMs));

  const showMenu = () => setMenuShown(true);
  const hideMenu = () => setMenuShown(false);
  useOnClickOutside(searchMenuRef, hideMenu);

  useEffect(() => {
    fetchMostUsedTokensAction();
  }, []);

  useEffect(() => {
    if (!reducerVal) setInputVal('');
  }, [reducerVal]);

  const getSymbol = (token) => getValueByMultiProps(token, ['token_symbol', 'symbol'])?.toLowerCase?.();

  const inputOnChangeCallback = useCallback(
    (e) => {
      setInputVal(e.target.value);
      handleChangeDebounced.current(e.target.value);
    },
    [handleChangeDebounced],
  );

  const selectedTokensSymbols = useMemo(() => selectedTokens.map((token) => getSymbol(token)), [selectedTokens]);

  const removeSelected = (_tokens = []) => _tokens.filter((token) => !selectedTokensSymbols.includes(getSymbol(token)));

  const renderedTokens = useMemo(
    () => removeSelected(!inputVal ? defaultTokens : searchResults),
    [inputVal, menuShown, searchResults, selectedTokensSymbols],
  );

  return (
    <div className={clsx('tokens-lists-search-wrapper', { 'show-menu': reducerVal })} ref={searchMenuRef}>
      <MagnifyingGlass />

      <input
        type="text"
        value={inputVal}
        className="search-input"
        placeholder={placeholder}
        onChange={inputOnChangeCallback}
        disabled={false}
        onFocus={showMenu}
        // onBlur={hideMenu}
      />
      <ErrorListner errorMsg={searchingErr} />

      {menuShown && (
        <div className="search-menu-list">
          {searching ? (
            <div className="menu-text-wrapper">{trans('pools.searching_tokens')}</div>
          ) : renderedTokens.length === 0 ? (
            <div className="menu-text-wrapper">{trans('pools.no_tokens_found')}</div>
          ) : (
            renderedTokens.map((token) => (
              <TokenListsSearchResult
                key={`${token.symbol}-${token.address}-${token.network}`}
                handleSearchResultClick={handleSearchResultClick}
                token={token}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

TokensListsSearch.defaultProps = {
  debounceMs: 400,
  placeholder: '',
  reducerVal: '',
  searching: false,
  searchingErr: '',
  searchResults: [],
  selectedTokens: [],
  defaultTokens: [],
};

TokensListsSearch.propTypes = {
  handleChange: PropTypes.func.isRequired,
  handleSearchResultClick: PropTypes.func.isRequired,
  debounceMs: PropTypes.number,
  placeholder: PropTypes.string,
  reducerVal: PropTypes.string,
  searching: PropTypes.bool,
  searchingErr: PropTypes.string,
  searchResults: PropTypes.array,
  selectedTokens: PropTypes.array,
  defaultTokens: PropTypes.array,
  fetchMostUsedTokensAction: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  fetchMostUsedTokensAction,
};

export default connect(null, mapDispatchToProps)(TokensListsSearch);
